/**
 * Domain-specific Invalidation event subclasses for `@tento/lona/sheets`.
 *
 * The core machinery (`Invalidation`, `InvalidationSink`,
 * `InvalidationSinkImpl`, `coalesce`, `Actor`) lives in
 * `@tento-core/invalidation` — it's generic and reused by any
 * consumer that needs path-based event routing.
 *
 * This module defines the row/sheet/cell event vocabulary on top of
 * that core. Every subclass carries a typed payload and a REST-style
 * `path` so subscribers can filter via
 * `Invalidation.matches(event, "rows.*.heightPx")` or by class
 * instance.
 */

import type { EventId } from "../ids/ids.ts";
import type { Option } from "../core/option.ts";
import type { DateTime, GenericRange, MsSinceEpoch, NaiveDate, Utc } from "@tento-chrono";
import {
  Actor,
  Invalidation,
  InvalidationSink,
  InvalidationSinkImpl,
  coalesce,
  pathMatches,
} from "@tento-core/invalidation";

import type { RowLocalId } from "../ids/row-local-id.ts";
import { type SyncKey, UserId } from "../ids/ids.ts";
import type { SheetId } from "../ids/sheet-id.ts";
import type { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import { SheetKeyRef } from "../ids/sheet-key-ref.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

// Re-export the core machinery so consumers get a single import
// surface (the subclasses + the sink + the Actor primitive).
export {
  Actor,
  Invalidation,
  InvalidationSink,
  InvalidationSinkImpl,
  coalesce,
  pathMatches,
};

// ── Singleton events (no payload) ──────────────────────────────────

/** The timezone preference changed. */
export class TimezoneChanged extends Invalidation {
  constructor(actor: Actor) {
    super(actor, "user.preferences.timezone");
  }
  eq(o: Invalidation): boolean {
    return o instanceof TimezoneChanged;
  }
}

/** The user's tasks changed. */
export class TasksChanged extends Invalidation {
  constructor(actor: Actor) {
    super(actor, "tasks");
  }
  eq(o: Invalidation): boolean {
    return o instanceof TasksChanged;
  }
}

/**
 * A user's scheduling links changed: a link created or edited through
 * `client.schedulingLinksFor(ownerId)`, stamped with the writer's actor.
 */
export class SchedulingLinksChanged extends Invalidation {
  constructor(
    actor: Actor,
    readonly ownerId: UserId,
  ) {
    super(actor, `scheduling-links.${ownerId}`);
  }
  eq(o: Invalidation): boolean {
    return o instanceof SchedulingLinksChanged && o.ownerId === this.ownerId;
  }
}

/** The sheet's layout changed, so geometry must be measured again. */
export class LayoutChanged extends Invalidation {
  constructor(actor: Actor) {
    super(actor, "ui.layout");
  }
  eq(o: Invalidation): boolean {
    return o instanceof LayoutChanged;
  }
}

/** The window (viewport) was resized. */
export class WindowResized extends Invalidation {
  constructor(actor: Actor) {
    super(actor, "ui.viewport");
  }
  eq(o: Invalidation): boolean {
    return o instanceof WindowResized;
  }
}

/** A sheet's column width changed. */
export class SheetWidthChanged extends Invalidation {
  /** `sheet`: the sheet, by id (a fixture's sheet not named by an id: by its key). */
  constructor(actor: Actor, readonly sheet: SheetKeyRef) {
    super(actor, `sheets.${SheetKeyRef.pathOf(sheet)}.width`);
  }
  eq(o: Invalidation): boolean {
    return o instanceof SheetWidthChanged && SheetKeyRef.equals(o.sheet, this.sheet);
  }
  /** Whether this event concerns the sheet `id`. */
  concerns(id: SheetId): boolean {
    return SheetKeyRef.names(this.sheet, id);
  }
}

/** The theme preference changed. */
export class ThemeChanged extends Invalidation {
  constructor(actor: Actor) {
    super(actor, "user.preferences.theme");
  }
  eq(o: Invalidation): boolean {
    return o instanceof ThemeChanged;
  }
}

/** The set of visible calendars changed. */
export class CalendarVisibilityChanged extends Invalidation {
  constructor(actor: Actor) {
    super(actor, "calendars.visibility");
  }
  eq(o: Invalidation): boolean {
    return o instanceof CalendarVisibilityChanged;
  }
}

/**
 * A row was added to a sheet's tree. Carries the inserted row's id AND the
 * OWNING sheet's id, so subscribers on the shared app-level sink advance only
 * their own sheet's structural state — pre-scope, an insert in sheet A bumped
 * every open sheet's render-cursor epoch, forcing sheet B to re-paint every
 * row it had already painted (the cross-sheet epoch storm). `sheetId: null`
 * means "unknown scope" and matches every sheet — the conservative fallback
 * for emitters that genuinely cannot name one, exactly as documented on
 * `SheetStructureChanged` below.
 */
export class NewRowInserted extends Invalidation {
  constructor(
    actor: Actor,
    readonly rowId: RowLocalId,
    readonly sheetKey: Option<SheetNetworkKey>,
  ) {
    super(actor, "sheets.*.rows");
  }
  eq(o: Invalidation): boolean {
    return o instanceof NewRowInserted && o.rowId === this.rowId &&
      o.sheetKey === this.sheetKey;
  }
  /** Whether this event concerns the sheet with `id` (unscoped matches all). */
  concerns(id: SheetNetworkKey): boolean {
    return this.sheetKey == null || this.sheetKey === id;
  }
}

/** Rows were reordered in a sheet. */
export class RowsReordered extends Invalidation {
  constructor(actor: Actor) {
    super(actor, "sheets.*.rows.reorder");
  }
  eq(o: Invalidation): boolean {
    return o instanceof RowsReordered;
  }
}

/** Every visible range of calendar events must be read again. */
export class ViewportsInvalidated extends Invalidation {
  constructor(actor: Actor) {
    super(actor, "calendars.events");
  }
  eq(o: Invalidation): boolean {
    return o instanceof ViewportsInvalidated;
  }
}

/**
 * A sheet's tree structure changed (reparent / delete / reorder / system-row
 * replace — id-less + coalesced). Carries the OWNING sheet's id so subscribers
 * on the shared app-level sink rebuild only their own sheet — pre-scope, sheet
 * A's structural storm rebuilt sheet B's joined tree too (every open sheet
 * paid every other sheet's changes). `sheetId: null` means "unknown scope" and
 * matches every sheet (the conservative fallback for emitters that genuinely
 * cannot name one); subscribers filter like `NewRowInserted`'s membership
 * check already does.
 */
export class SheetStructureChanged extends Invalidation {
  /** `sheetId`: the sheet whose structure changed; `null`: unscoped (every sheet). */
  constructor(actor: Actor, readonly sheetId: Option<SheetId>) {
    super(actor, "sheets.*.structure");
  }
  eq(o: Invalidation): boolean {
    return o instanceof SheetStructureChanged && o.sheetId === this.sheetId;
  }
  /**
   * Whether this event concerns the sheet `id` — unscoped matches all, and a
   * listener whose sheet has no id (`null`: a fixture sheet named by other
   * text) takes every event.
   */
  concerns(id: Option<SheetId>): boolean {
    return this.sheetId == null || id == null || this.sheetId.equals(id);
  }
}

/**
 * Which calendar preference changed — mirrors the setters on
 * `CalendarPreferences` (src/preferences/calendar-preferences.ts).
 *
 * ROUTING LAW: this event carries the kind in its PATH
 * (`user.preferences.calendar.<kind>`, see
 * `CalendarPreferencesChanged.pathFor`) and classifies nothing.
 * Each preference is routed by the nodes that CONSUME it, via
 * ordinary path matching — alias templates declare row-scoped
 * dependencies (`AliasTemplate.reexpandOn`, wired generically by
 * `wireDeclaredReexpand`) and the render layer registers geometry
 * kinds as plain `.when(pathFor(kind), …)` sink rules
 * (`RENDER_GEOMETRY_PREFERENCES` in @tento/lona/sheets-ui
 * sheet-invalidation-effects). Every kind MUST have at least one
 * declared consumer — enforced by the invalidation-pipeline routing
 * suite, which replaces the exhaustive switch that used to live here.
 */
export type CalendarPreferenceKind =
  | "firstDay"
  | "timeFormat"
  | "dateFormat"
  | "secondaryTimezones"
  | "secondaryCalendars"
  | "defaultWeatherLocation";

/** Value mirror of `CalendarPreferenceKind` for exhaustiveness tests. */
export const CALENDAR_PREFERENCE_KINDS: readonly CalendarPreferenceKind[] = deepFrozen([
  "firstDay",
  "timeFormat",
  "dateFormat",
  "secondaryTimezones",
  "secondaryCalendars",
  "defaultWeatherLocation",
]);

/**
 * A calendar preference changed (first day, time or date format, calendar
 * systems, weather location).
 */
export class CalendarPreferencesChanged extends Invalidation {
  /** Path root every kind emits under (`user.preferences.calendar.<kind>`). */
  static readonly PATH_ROOT = "user.preferences.calendar";
  /**
   * The invalidation path a preference kind emits — the address
   * consumers declare against (app `.when(...)` rules, template
   * `reexpandOn` patterns). Kind-in-path means coarse subscribers to
   * `PATH_ROOT` still match via parent subsumption, while per-kind
   * routing needs no `instanceof` special case.
   */
  static pathFor(kind: CalendarPreferenceKind): string {
    return `${CalendarPreferencesChanged.PATH_ROOT}.${kind}`;
  }
  constructor(actor: Actor, readonly preference: CalendarPreferenceKind) {
    super(actor, CalendarPreferencesChanged.pathFor(preference));
  }
  eq(o: Invalidation): boolean {
    return (
      o instanceof CalendarPreferencesChanged &&
      o.preference === this.preference
    );
  }
}

/** The user's list of sheets changed. */
export class SheetListChanged extends Invalidation {
  constructor(actor: Actor) {
    super(actor, "sheets");
  }
  eq(o: Invalidation): boolean {
    return o instanceof SheetListChanged;
  }
}

/**
 * Emitted when a specific sheet is deleted server-side. App-layer
 * code that needs to react to "the sheet I'm looking at was deleted"
 * listens for this event instead of inferring it from the absence
 * of `sdk.sheets.peek(id)` after a generic `SheetListChanged` (which
 * has false positives for ephemerals — they're never in the
 * server-list cache by design).
 */
export class SheetDeleted extends Invalidation {
  constructor(actor: Actor, readonly sheetId: SheetId) {
    super(actor, `sheets.${sheetId}.deleted`);
  }
  eq(o: Invalidation): boolean {
    return o instanceof SheetDeleted && o.sheetId.equals(this.sheetId);
  }
}

// ── Scoped events (payload-bearing) ────────────────────────────────

/**
 * A sheet's metadata (title, description, settings) changed. `sheet` names
 * it by id — resolved before the emit — or, where the writer knows it only
 * by a lookup key (a `sheets.update(":journal", …)` before the list has read
 * the sheet), by that key and its user.
 */
export class SheetMetadataChanged extends Invalidation {
  constructor(actor: Actor, readonly sheet: SheetKeyRef) {
    super(actor, `sheets.${SheetKeyRef.pathOf(sheet)}`);
  }
  eq(o: Invalidation): boolean {
    return o instanceof SheetMetadataChanged && SheetKeyRef.equals(o.sheet, this.sheet);
  }
  /** Whether this event concerns the sheet `id` (a key-only event names no id). */
  concerns(id: SheetId): boolean {
    return SheetKeyRef.names(this.sheet, id);
  }
}

/** A row's height changed (a height write, a re-read, a stored height): `rowId` names the row to re-measure. */
export class RowHeightChanged extends Invalidation {
  constructor(actor: Actor, readonly rowId: RowLocalId) {
    super(actor, `rows.${rowId}.heightPx`);
  }
  eq(o: Invalidation): boolean {
    return o instanceof RowHeightChanged && o.rowId === this.rowId;
  }
}

/** A row's data changed, for the whole row or for the dates it names. */
export class RowDataChanged extends Invalidation {
  /**
   * The dates the change is known to cover, or null for the whole row —
   * the meaning every existing emitter has. A ranged event lets a renderer
   * skip columns the change cannot have touched (an install for dates
   * outside the rendered strip used to re-bind the row in every rendered
   * column); a whole-row event still rebinds everything. The two never
   * coalesce into each other: `eq` compares the range, so a whole-row
   * event survives beside a ranged one and the consumer lets it dominate.
   */
  readonly range: Option<NaiveDate.Range> = null;
  constructor(
    actor: Actor,
    readonly rowId: RowLocalId,
    readonly field: Option<string>,
  ) {
    super(actor, field ? `rows.${rowId}.${field}` : `rows.${rowId}`);
  }
  eq(o: Invalidation): boolean {
    return (
      o instanceof RowDataChanged &&
      o.rowId === this.rowId &&
      o.field === this.field &&
      RowDataChanged.sameRange(o.range, this.range)
    );
  }
  static sameRange(a: Option<NaiveDate.Range>, b: Option<NaiveDate.Range>): boolean {
    if (a == null || b == null) return a == null && b == null;
    return a.start.dse === b.start.dse && a.end.dse === b.end.dse;
  }
}

/**
 * A row's cell data changed within a known date range (end exclusive) —
 * the shape the cell accessor's bulk fetch produces. `instanceof
 * RowDataChanged` holds, so a consumer reads `range` off every row-data
 * event and treats null as the whole row.
 */
export class RowRangeDataChanged extends RowDataChanged {
  override readonly range: NaiveDate.Range;
  constructor(actor: Actor, rowId: RowLocalId, range: NaiveDate.Range) {
    super(actor, rowId, null);
    this.range = range;
  }
}

/**
 * A read first covered `range` (end exclusive) of a row and carried no cells
 * for it. No cell changed, so it is not a `RowDataChanged` and nothing
 * repaints for it (its path is outside `rows.*`); what waits on a row's
 * first coverage rather than on its cells (an unhydrated alias re-polling a
 * host input when its provider rows first load) hears it here.
 */
export class RowRangeCovered extends Invalidation {
  constructor(actor: Actor, readonly rowId: RowLocalId, readonly range: NaiveDate.Range) {
    super(actor, `coverage.rows.${rowId}`);
  }
  eq(o: Invalidation): boolean {
    return o instanceof RowRangeCovered && o.rowId === this.rowId && RowDataChanged.sameRange(o.range, this.range);
  }
}

/**
 * The data of one column changed, for one row (`rowId`) or for every row
 * (`rowId` null — a column of data arrived or was repainted from elsewhere,
 * `platformOf(client).invalidateColumn(column)`). It is the one column-values
 * event: a sheets-ui sink routes it to the column's content facet only.
 *
 * A datetime (custom) cell is stored under the UTC date its start falls on.
 * A store change announces it under that date (`column`, the key a row's
 * events facade re-reads by), once, like a date-keyed cell's, even when the
 * cell runs past that date's midnight (`columnDataChangedOf`); a row's
 * timepoint write announces each UTC date its ranges touch, one event per
 * date (`RowTimepoints`). Either way `column` names a UTC date, not every
 * day the cell is in: a paint surface whose day columns are a viewer's days
 * east or west of UTC holds the cell in a column that date does not name
 * (07:00 in Asia/Tokyo is the previous UTC date), so the event also carries
 * the cell's `instants` (`ColumnDataChanged.timed`): the surface rebinds its
 * own columns those instants meet (`scopeOf("cell-content-column").span`),
 * never from `column` alone.
 */
export class ColumnDataChanged extends Invalidation {
  #instants: Option<{ readonly start: number; readonly end: number }> = null;
  constructor(
    actor: Actor,
    readonly column: NaiveDate.Partial,
    readonly rowId: Option<RowLocalId>,
  ) {
    super(
      actor,
      rowId ? `rows.${rowId}.cells.${column}` : `rows.*.cells.${column}`,
    );
  }

  /**
   * The change of a datetime cell of `rowId` spanning `instants`, announced
   * under `column`, a UTC date: a store change, once, under the date the
   * cell is stored under (its start's), however many UTC dates the instants
   * cross; a row's timepoint write, once per UTC date its ranges touch
   * (`UtcDays.touched`: a range ending at a UTC midnight does not touch that
   * date), each with the hull of its ranges on that date. The instants, not
   * `column`, say which days the cell is in.
   */
  static timed(actor: Actor, column: NaiveDate.Partial, rowId: RowLocalId, instants: DateTime.Range<Utc>): ColumnDataChanged {
    const event = new ColumnDataChanged(actor, column, rowId);
    event.#instants = Object.freeze({ start: instants.start.mse, end: instants.end.mse });
    return event;
  }

  /**
   * The instants of the datetime cell this change is for
   * (`ColumnDataChanged.timed`), as milliseconds since the epoch, start
   * inclusive and end exclusive, in which a paint surface finds the columns
   * of its own (the viewer's) days to rebind; `null` for a date-keyed cell,
   * a fetch settle or a whole column (`column` alone names those).
   */
  get instants(): Option<{ readonly start: number; readonly end: number }> {
    return this.#instants;
  }

  eq(o: Invalidation): boolean {
    if (!(o instanceof ColumnDataChanged) || !this.column.equals(o.column) || this.rowId !== o.rowId) return false;
    // Two cells of one row and date at different instants are two repaints: a viewer may hold them in different columns.
    const a = this.#instants, b = o.instants;
    return a == null ? b == null : b != null && a.start === b.start && a.end === b.end;
  }
}

/**
 * An event's timezone field changed (or a timezone-carrying event
 * was inserted / deleted). Carries before/after values so the
 * handler doesn't need per-event diffing of its own.
 */
export class EventTimezoneChanged extends Invalidation {
  constructor(
    actor: Actor,
    readonly eventId: EventId,
    readonly prev: Option<string>,
    readonly next: Option<string>,
  ) {
    super(actor, `calendars.events.${eventId}.timezone`);
  }
  eq(o: Invalidation): boolean {
    return (
      o instanceof EventTimezoneChanged &&
      o.eventId === this.eventId &&
      o.prev === this.prev &&
      o.next === this.next
    );
  }
}

/** Events in a time range changed. */
export class TimepointsChanged extends Invalidation {
  constructor(actor: Actor, readonly range: GenericRange<MsSinceEpoch>) {
    super(actor, `calendars.events.${range.start}-${range.end}`);
  }
  eq(o: Invalidation): boolean {
    return (
      o instanceof TimepointsChanged &&
      o.range.start === this.range.start &&
      o.range.end === this.range.end
    );
  }
}

/** A row's legend (its label column) changed. */
export class RowLegendChanged extends Invalidation {
  constructor(
    actor: Actor,
    readonly rowId: RowLocalId,
    readonly prev: Option<string>,
    readonly next: Option<string>,
  ) {
    super(actor, `rows.${rowId}.label`);
  }
  eq(o: Invalidation): boolean {
    return (
      o instanceof RowLegendChanged &&
      o.rowId === this.rowId &&
      o.prev === this.prev &&
      o.next === this.next
    );
  }
}

/**
 * A row's sync state changed. `syncKey` is present when one specific
 * per-key status flipped; null when the aggregate re-rolled without a
 * single dominant key (e.g. `setKeys` reseeded the state).
 */
export class RowSyncStateChanged extends Invalidation {
  constructor(
    actor: Actor,
    readonly rowId: RowLocalId,
    readonly syncKey: Option<SyncKey>,
  ) {
    super(
      actor,
      syncKey === null
        ? `rows.${rowId}.syncState`
        : `rows.${rowId}.syncState.${syncKey}`,
    );
  }
  eq(o: Invalidation): boolean {
    return (
      o instanceof RowSyncStateChanged &&
      o.rowId === this.rowId &&
      o.syncKey === this.syncKey
    );
  }
}

